import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Card,
  Group,
  LoadingOverlay,
  NumberInput,
  Paper,
  Select,
  SimpleGrid,
  Stack,
  Switch,
  Table,
  Tabs,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { QRCodeCanvas } from "qrcode.react";
import { useNotification } from "../adminProjectNotifications/popupnotifications";
import { API_BASE_URL } from "../../config/baseUrls";

// MUSE x Ota TOKYO stamp rally: live numbers, volunteer QR sheets, prize pool, booth redeem, the daily draw and contact export.
// Backend: /stamp-rally/admin/* (admin only, entries carry contact details).

const headers = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
});

const api = async (path, options = {}) => {
  const res = await fetch(`${API_BASE_URL}/stamp-rally/admin${path}`, { headers: headers(), ...options });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data.success) throw new Error(data.error || data.message || "Request failed");
  return data.data;
};

const fmt = (iso) =>
  iso
    ? new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })
    : "-";

export default function StampRallyPage() {
  const [rallies, setRallies] = useState([]);
  const [rallyId, setRallyId] = useState(null);
  const [summary, setSummary] = useState([]);
  const [entries, setEntries] = useState([]);
  const [day, setDay] = useState("");
  const [prizeData, setPrizeData] = useState({ prizes: [], noWin: 0 });
  const [newPrize, setNewPrize] = useState({ name: "", stock: 10, weight: 1 });
  const [redeemQuery, setRedeemQuery] = useState("");
  const [claims, setClaims] = useState(null);
  const [loading, setLoading] = useState(false);
  const { showError, showSuccess } = useNotification();

  const rally = useMemo(() => rallies.find((r) => r.id === rallyId), [rallies, rallyId]);

  const run = useCallback(
    async (fn, okMessage) => {
      setLoading(true);
      try {
        const result = await fn();
        if (okMessage) showSuccess("Done", okMessage);
        return result;
      } catch (e) {
        showError("Error", e.message);
        return undefined;
      } finally {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const loadRallies = useCallback(async () => {
    const list = await run(() => api("/rallies"));
    if (!list) return;
    setRallies(list);
    setRallyId((current) => current || list[0]?.id || null);
  }, [run]);

  const loadRally = useCallback(async () => {
    if (!rallyId) return;
    const [s, e, p] = await Promise.all([
      run(() => api(`/summary/${rallyId}`)),
      run(() => api(`/entries/${rallyId}${day ? `?day=${day}` : ""}`)),
      run(() => api(`/prizes/${rallyId}`)),
    ]);
    if (s) setSummary(s);
    if (e) setEntries(e);
    if (p) setPrizeData(p);
  }, [rallyId, day, run]);

  useEffect(() => {
    loadRallies();
  }, [loadRallies]);
  useEffect(() => {
    loadRally();
  }, [loadRally]);

  const patchRally = (body, message) =>
    run(async () => {
      await api(`/rallies/${rallyId}`, { method: "PATCH", body: JSON.stringify(body) });
      await loadRallies();
    }, message);

  const draw = async (istDay) => {
    const winners = await run(
      () => api("/draw", { method: "POST", body: JSON.stringify({ rallyId, istDay }) }),
      "Winners drawn",
    );
    if (winners) loadRally();
  };

  const winnerAction = async (winnerId, action, message) => {
    await run(() => api(`/winners/${winnerId}/${action}`, { method: "POST" }), message);
    loadRally();
  };

  const cfg = rally?.config || {};
  const instant = cfg.drawMode === "instant";
  const setConfig = (patch, message = "Updated") => patchRally({ config: { ...cfg, ...patch } }, message);

  const savePrize = async (id, body) => {
    await run(() => api(`/prizes/${id}`, { method: "PATCH", body: JSON.stringify(body) }), "Prize updated");
    loadRally();
  };

  const addPrize = async () => {
    if (!newPrize.name.trim()) return;
    const created = await run(
      () => api("/prizes", { method: "POST", body: JSON.stringify({ rallyId, ...newPrize, name: newPrize.name.trim() }) }),
      "Prize added",
    );
    if (created) {
      setNewPrize({ name: "", stock: 10, weight: 1 });
      loadRally();
    }
  };

  const searchClaims = async () => {
    const found = await run(() => api(`/claims/${rallyId}?q=${encodeURIComponent(redeemQuery.trim())}`));
    if (found) setClaims(found);
  };

  const redeem = async (redeemCode, allowExpired = false) => {
    if (allowExpired && !window.confirm("This prize has expired. Hand it over anyway?")) return;
    try {
      setLoading(true);
      await api("/redeem", { method: "POST", body: JSON.stringify({ redeemCode, allowExpired }) });
      showSuccess("Redeemed", "Hand over the prize");
    } catch (e) {
      showError("Cannot redeem", e.message);
    } finally {
      setLoading(false);
    }
    await searchClaims();
    loadRally();
  };

  const rotate = async (pointId) => {
    if (!window.confirm("Rotate this QR code? The printed QR stops working immediately.")) return;
    await run(() => api(`/points/${pointId}/rotate-code`, { method: "POST" }), "New code created, reprint this sheet");
    loadRallies();
  };

  const exportCsv = () =>
    run(async () => {
      const res = await fetch(`${API_BASE_URL}/stamp-rally/admin/entries/${rallyId}/export.csv${day ? `?day=${day}` : ""}`, {
        headers: headers(),
      });
      if (!res.ok) throw new Error("Export failed");
      const url = URL.createObjectURL(await res.blob());
      const a = document.createElement("a");
      a.href = url;
      a.download = `stamp-rally-${day || "all"}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    });

  const days = useMemo(() => [...new Set([...summary.map((s) => s.istDay), ...entries.map((e) => e.istDay)])].sort(), [summary, entries]);
  const winnersPerDay = rally?.config?.winnersPerDay ?? 2;

  return (
    <Box p="md" pos="relative">
      <style>{`@media print { .no-print { display: none !important; } }`}</style>
      <LoadingOverlay visible={loading} />
      <Group justify="space-between" mb="md">
        <Title order={3}>Stamp Rally</Title>
        <Select
          w={320}
          data={rallies.map((r) => ({ value: r.id, label: `${r.title} (${r.audience === "ALL" ? "public" : "admins only"})` }))}
          value={rallyId}
          onChange={setRallyId}
          placeholder="No rally yet. Run scripts/seedStampRally.js"
        />
      </Group>

      {!rally ? (
        <Text c="dimmed">No rally found.</Text>
      ) : (
        <Tabs defaultValue="overview">
          <Tabs.List mb="md">
            <Tabs.Tab value="overview">Overview</Tabs.Tab>
            {instant && <Tabs.Tab value="prizes">Prizes</Tabs.Tab>}
            {instant && <Tabs.Tab value="redeem">Redeem at booth</Tabs.Tab>}
            <Tabs.Tab value="draw">{instant ? "Entries" : "Draw and entries"}</Tabs.Tab>
            <Tabs.Tab value="panels">Panels and QR sheets</Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="overview">
            <Stack>
              <Paper withBorder p="md">
                <Group justify="space-between" align="flex-end">
                  <Stack gap={4}>
                    <Switch
                      label="Rally is live in the app"
                      checked={rally.isActive}
                      onChange={(e) => patchRally({ isActive: e.currentTarget.checked }, "Updated")}
                    />
                    <Switch
                      label="Visible to everyone (off = admins only, for testing)"
                      checked={rally.audience === "ALL"}
                      onChange={(e) => patchRally({ audience: e.currentTarget.checked ? "ALL" : "ADMIN_ONLY" }, "Updated")}
                    />
                    <Text size="sm" c="dimmed">
                      Runs {fmt(rally.startsAt)} to {fmt(rally.endsAt)}
                    </Text>
                  </Stack>
                  <Select
                    label="Prize mode"
                    w={260}
                    allowDeselect={false}
                    data={[
                      { value: "daily", label: "Daily draw (admin picks winners)" },
                      { value: "instant", label: "Instant prize on completion" },
                    ]}
                    value={cfg.drawMode || "daily"}
                    onChange={(v) => v && v !== (cfg.drawMode || "daily") && setConfig({ drawMode: v })}
                  />
                  <NumberInput
                    label="Stamps needed to complete"
                    w={200}
                    min={1}
                    max={rally.points.length}
                    value={rally.requiredCount}
                    onChange={(v) => v && v !== rally.requiredCount && patchRally({ requiredCount: Number(v) }, "Updated")}
                  />
                </Group>
              </Paper>

              {summary.length === 0 && <Text c="dimmed">No stamps yet.</Text>}
              {summary.map((d) => (
                <Paper key={d.istDay} withBorder p="md">
                  <Group justify="space-between" mb="xs">
                    <Title order={5}>{d.istDay}</Title>
                    <Group gap="xs">
                      <Badge variant="light">{d.participants} players</Badge>
                      <Badge color="green" variant="light">{d.completions} complete</Badge>
                      <Badge color="grape" variant="light">{d.winners} winners ({d.claimed} claimed)</Badge>
                    </Group>
                  </Group>
                  <Table>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>Panel</Table.Th>
                        <Table.Th>Anime</Table.Th>
                        <Table.Th>Stamps given</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {rally.points.map((p) => (
                        <Table.Tr key={p.id}>
                          <Table.Td>{p.name}</Table.Td>
                          <Table.Td>{p.ipTitle}</Table.Td>
                          <Table.Td>{d.stampsByPoint[p.id] || 0}</Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                </Paper>
              ))}
            </Stack>
          </Tabs.Panel>

          <Tabs.Panel value="prizes">
            <Stack>
              <Paper withBorder p="md">
                <Group align="flex-end">
                  <NumberInput
                    label="Prize valid for (minutes)"
                    w={200}
                    min={5}
                    max={1440}
                    value={cfg.redeemWindowMinutes ?? 180}
                    onChange={(v) => v && Number(v) !== (cfg.redeemWindowMinutes ?? 180) && setConfig({ redeemWindowMinutes: Number(v) })}
                  />
                  <NumberInput
                    label="Chance weight of no prize"
                    description="0 = everyone wins while stock lasts"
                    w={240}
                    min={0}
                    value={cfg.noWinWeight ?? 0}
                    onChange={(v) => v !== "" && Number(v) !== (cfg.noWinWeight ?? 0) && setConfig({ noWinWeight: Number(v) })}
                  />
                  <Switch
                    label="One prize per person for the whole rally"
                    checked={cfg.onePerUser !== false}
                    onChange={(e) => setConfig({ onePerUser: e.currentTarget.checked })}
                  />
                  <Switch
                    label="Also give points, gacha ticket and badge"
                    checked={cfg.pointsEnabled !== false}
                    onChange={(e) => setConfig({ pointsEnabled: e.currentTarget.checked })}
                  />
                </Group>
              </Paper>
              <Table>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Prize</Table.Th>
                    <Table.Th>Stock left</Table.Th>
                    <Table.Th>Odds weight</Table.Th>
                    <Table.Th>Won</Table.Th>
                    <Table.Th>Handed over</Table.Th>
                    <Table.Th>On</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {prizeData.prizes.map((p) => (
                    <Table.Tr key={p.id}>
                      <Table.Td>{p.name}</Table.Td>
                      <Table.Td>
                        <NumberInput
                          w={100}
                          min={0}
                          key={`${p.id}-${p.stock}`}
                          defaultValue={p.stock}
                          onBlur={(e) => e.currentTarget.value !== "" && Number(e.currentTarget.value) !== p.stock && savePrize(p.id, { stock: Number(e.currentTarget.value) })}
                        />
                      </Table.Td>
                      <Table.Td>
                        <NumberInput
                          w={90}
                          min={0}
                          key={`${p.id}-w${p.weight}`}
                          defaultValue={p.weight}
                          onBlur={(e) => e.currentTarget.value !== "" && Number(e.currentTarget.value) !== p.weight && savePrize(p.id, { weight: Number(e.currentTarget.value) })}
                        />
                      </Table.Td>
                      <Table.Td>{p.drawn}</Table.Td>
                      <Table.Td>{p.redeemed}</Table.Td>
                      <Table.Td>
                        <Switch checked={p.isActive} onChange={(e) => savePrize(p.id, { isActive: e.currentTarget.checked })} />
                      </Table.Td>
                    </Table.Tr>
                  ))}
                  <Table.Tr>
                    <Table.Td colSpan={6}>
                      <Text size="sm" c="dimmed">Drew no prize: {prizeData.noWin}. A prize with stock 0 or switched off is skipped. Lowering stock below what is already won is fine.</Text>
                    </Table.Td>
                  </Table.Tr>
                </Table.Tbody>
              </Table>
              <Paper withBorder p="md">
                <Group align="flex-end">
                  <TextInput label="New prize" placeholder="Name" value={newPrize.name} onChange={(e) => setNewPrize({ ...newPrize, name: e.currentTarget.value })} />
                  <NumberInput label="Stock" w={100} min={0} value={newPrize.stock} onChange={(v) => setNewPrize({ ...newPrize, stock: Number(v) || 0 })} />
                  <NumberInput label="Odds weight" w={110} min={0} value={newPrize.weight} onChange={(v) => setNewPrize({ ...newPrize, weight: Number(v) || 0 })} />
                  <Button onClick={addPrize}>Add prize</Button>
                </Group>
              </Paper>
            </Stack>
          </Tabs.Panel>

          <Tabs.Panel value="redeem">
            <Stack>
              <Text size="sm" c="dimmed">
                The winner shows the pop-up with a 6 character code. Type the code (or search by phone, name or email), check the prize, press Redeem. A prize can only be redeemed once.
              </Text>
              <Group align="flex-end">
                <TextInput
                  label="Code, phone, name or email"
                  w={320}
                  value={redeemQuery}
                  onChange={(e) => setRedeemQuery(e.currentTarget.value)}
                  onKeyDown={(e) => e.key === "Enter" && searchClaims()}
                />
                <Button onClick={searchClaims}>Find</Button>
              </Group>
              {claims && claims.length === 0 && <Text c="dimmed">Nothing found.</Text>}
              {claims && claims.length > 0 && (
                <Table>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Code</Table.Th>
                      <Table.Th>Prize</Table.Th>
                      <Table.Th>Who</Table.Th>
                      <Table.Th>Valid until</Table.Th>
                      <Table.Th>Status</Table.Th>
                      <Table.Th />
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {claims.map((c) => (
                      <Table.Tr key={c.entryId}>
                        <Table.Td><Text ff="monospace" fw={700}>{c.redeemCode}</Text></Table.Td>
                        <Table.Td>{c.prize}</Table.Td>
                        <Table.Td>{[c.name, c.phone || c.email].filter(Boolean).join(" / ") || "-"}</Table.Td>
                        <Table.Td>{fmt(c.expiresAt)}</Table.Td>
                        <Table.Td>
                          <Badge color={c.status === "REDEEMED" ? "green" : c.status === "EXPIRED" ? "gray" : "grape"}>
                            {c.status === "REDEEMED" ? `Given ${fmt(c.redeemedAt)}` : c.status === "EXPIRED" ? "Expired" : "Not yet given"}
                          </Badge>
                        </Table.Td>
                        <Table.Td>
                          {c.status === "UNREDEEMED" && <Button size="compact-sm" onClick={() => redeem(c.redeemCode)}>Redeem</Button>}
                          {c.status === "EXPIRED" && (
                            <Button size="compact-sm" color="red" variant="light" onClick={() => redeem(c.redeemCode, true)}>
                              Redeem anyway
                            </Button>
                          )}
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              )}
            </Stack>
          </Tabs.Panel>

          <Tabs.Panel value="draw">
            <Stack>
              <Group>
                <Select
                  label="Day"
                  clearable
                  placeholder="All days"
                  data={days}
                  value={day || null}
                  onChange={(v) => setDay(v || "")}
                />
                <Button mt={24} variant="default" onClick={exportCsv}>Download CSV</Button>
                {day && (
                  <Button mt={24} onClick={() => draw(day)}>
                    Draw {winnersPerDay} winners for {day}
                  </Button>
                )}
              </Group>
              {!day && <Text size="sm" c="dimmed">Pick a day to draw winners. Voided winners free a slot and are not drawn again.</Text>}
              <Table>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Day</Table.Th>
                    <Table.Th>Completed</Table.Th>
                    <Table.Th>Name</Table.Th>
                    <Table.Th>Email</Table.Th>
                    <Table.Th>Phone</Table.Th>
                    <Table.Th>Winner</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {entries.map((e) => (
                    <Table.Tr key={e.id}>
                      <Table.Td>{e.istDay}</Table.Td>
                      <Table.Td>{fmt(e.completedAt)}</Table.Td>
                      <Table.Td>{e.contactName || "-"}</Table.Td>
                      <Table.Td>{e.contactEmail || "-"}</Table.Td>
                      <Table.Td>{e.contactPhone || "-"}</Table.Td>
                      <Table.Td>
                        {e.winner ? (
                          <Group gap="xs">
                            {e.winner.voidedAt ? (
                              <Badge color="gray">Voided</Badge>
                            ) : e.winner.claimedAt ? (
                              <Badge color="green">Prize given</Badge>
                            ) : (
                              <Badge color="grape">Winner</Badge>
                            )}
                            {!e.winner.voidedAt && !e.winner.claimedAt && (
                              <Button size="compact-xs" onClick={() => winnerAction(e.winner.id, "claim", "Marked as given")}>
                                Mark given
                              </Button>
                            )}
                            {!e.winner.voidedAt && !e.winner.claimedAt && (
                              <Button size="compact-xs" color="red" variant="light" onClick={() => winnerAction(e.winner.id, "void", "Winner voided")}>
                                Void
                              </Button>
                            )}
                          </Group>
                        ) : (
                          "-"
                        )}
                      </Table.Td>
                    </Table.Tr>
                  ))}
                  {entries.length === 0 && (
                    <Table.Tr>
                      <Table.Td colSpan={6}>
                        <Text c="dimmed">No completed cards yet.</Text>
                      </Table.Td>
                    </Table.Tr>
                  )}
                </Table.Tbody>
              </Table>
            </Stack>
          </Tabs.Panel>

          <Tabs.Panel value="panels">
            <Stack>
              <Group justify="space-between">
                <Text size="sm" c="dimmed">
                  Print this page (volunteers hold one sheet per panel). These QR codes are secret: do not post them anywhere.
                </Text>
                <Button variant="default" onClick={() => window.print()}>Print sheets</Button>
              </Group>
              <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }}>
                {rally.points.map((p) => (
                  <Card key={p.id} withBorder padding="lg" style={{ breakInside: "avoid" }}>
                    <Stack align="center" gap="xs">
                      <Title order={4} ta="center">{p.name}</Title>
                      <Text size="sm" ta="center" fw={600}>{p.ipTitle}</Text>
                      <QRCodeCanvas value={`OTASTAMP:v1:${p.code}`} size={220} level="M" includeMargin />
                      <Text size="sm" ta="center">Show your story, get your stamp</Text>
                      <Group gap="xs" className="no-print">
                        {!p.isActive && <Badge color="gray">Off</Badge>}
                        <Button size="compact-xs" color="red" variant="light" onClick={() => rotate(p.id)}>
                          Rotate code
                        </Button>
                      </Group>
                    </Stack>
                  </Card>
                ))}
              </SimpleGrid>
            </Stack>
          </Tabs.Panel>
        </Tabs>
      )}
    </Box>
  );
}
